/**
 * Salon-local DATE math for the customer menu's split (#114).
 *
 * René's rule: a booking goes to Historial when its DATE is past in the
 * SALON's timezone — same-day past hours stay upcoming. The instant lives in
 * `bdt` (ISO); which wall-clock day it means belongs to each booking's OWN
 * salon (cross-salon union, api-salon#543), never the viewer's clock — the
 * #379 class of bugs.
 */

/**
 * The salon-local calendar date of an instant, as a sortable YYYY-MM-DD key.
 * Assembled from formatToParts — a locale's rendered ORDER is not a spec
 * guarantee across engines (Codex), but the parts' values are. Empty string
 * when the instant or timezone is unusable.
 */
export function localDateKey(instant, tz) {
  if (!instant) return '';
  try {
    const date = new Date(instant);
    if (Number.isNaN(date.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const part = (type) => parts.find((p) => p.type === type)?.value || '';
    const key = `${part('year')}-${part('month')}-${part('day')}`;
    return key === '--' ? '' : key;
  } catch {
    return '';
  }
}

/**
 * Whether the booking's salon-local DATE is before today (salon-local).
 * An unusable date answers false — a booking the split cannot place stays
 * visible in upcoming rather than silently buried in a collapsed section.
 */
export function isPastDate(bdt, tz, now = new Date()) {
  const day = localDateKey(bdt, tz);
  const today = localDateKey(now, tz);
  return !!day && !!today && day < today;
}
